import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../../components/Card';
import Button from '../../components/Button';
import TaxonomyTree from '../../components/taxonomy/TaxonomyTree';
import TaxonomyNodeCard from '../../components/taxonomy/TaxonomyNodeCard';
import TaxonomyNodeForm from '../../components/taxonomy/TaxonomyNodeForm';
import AITaxonomyGenerator from '../../components/taxonomy/AITaxonomyGenerator';
import TaxonomyJobProgress from '../../components/taxonomy/TaxonomyJobProgress';
import { apiClient } from '../../lib/api';
import { useAuthStore } from '../../lib/auth';

// Type definitions matching backend schema
export interface TaxonomyNode {
  id: string;
  workspaceId: string;
  parentId: string | null;
  name: string;
  description: string | null;
  level: number;
  path: string;
  icon: string | null;
  color: string | null;
  createdAt: string;
  updatedAt: string;
  children?: TaxonomyNode[];
  keywords?: TaxonomyKeyword[];
  linkedContentCount?: number;
  breadcrumbNames?: string[];
  specificItems?: string[] | null;
}

export interface TaxonomyKeyword {
  id: string;
  nodeId: string;
  term: string;
  relevanceScore: number;
  createdAt: string;
}

export default function TaxonomySettingsPage() {
  const { t } = useTranslation();
  const { workspace } = useAuthStore();
  const [nodes, setNodes] = useState<TaxonomyNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<TaxonomyNode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showNodeForm, setShowNodeForm] = useState(false);
  const [editingNode, setEditingNode] = useState<TaxonomyNode | null>(null);
  const [parentForNewNode, setParentForNewNode] = useState<TaxonomyNode | null>(null);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  // Fetch taxonomy tree
  const fetchTaxonomy = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/taxonomy', {
        params: {
          includeKeywords: true,
        },
      });
      // Backend returns {nodes: [...], totalNodes: number, maxDepth: number}
      setNodes(response.data.nodes || []);
      setError('');
    } catch (err: any) {
      console.error('Failed to fetch taxonomy:', err);
      setError('Failed to load taxonomy');
    } finally {
      setIsLoading(false);
    }
  };

  // Check for active AI generation job
  const checkActiveJob = async () => {
    try {
      const response = await apiClient.get('/taxonomy/generate-ai/active');
      const activeJob = response.data.data;
      if (activeJob && (activeJob.state === 'waiting' || activeJob.state === 'active')) {
        setActiveJobId(activeJob.jobId);
      } else {
        setActiveJobId(null);
      }
    } catch (err) {
      console.error('Failed to check active job:', err);
      setActiveJobId(null);
    }
  };

  useEffect(() => {
    if (workspace?.id) {
      fetchTaxonomy();
      checkActiveJob();
    }
  }, [workspace?.id]);

  const handleCreateFirstNode = () => {
    setEditingNode(null);
    setParentForNewNode(null);
    setShowNodeForm(true);
  };

  const handleCreateChild = (parentNode: TaxonomyNode) => {
    setEditingNode(null);
    setParentForNewNode(parentNode);
    setShowNodeForm(true);
  };

  const handleEditNode = (node: TaxonomyNode) => {
    setEditingNode(node);
    setParentForNewNode(null);
    setShowNodeForm(true);
  };

  const handleFormSubmit = async (data: any) => {
    setIsSubmittingForm(true);
    setError('');
    setSuccessMessage('');

    try {
      if (editingNode) {
        // Update existing node
        await apiClient.patch(`/taxonomy/${editingNode.id}`, data);
        setSuccessMessage(`Category "${data.name}" updated successfully`);
      } else {
        // Create new node
        await apiClient.post('/taxonomy', data);
        setSuccessMessage(`Category "${data.name}" created successfully`);
      }

      // Close form and refresh
      setShowNodeForm(false);
      setEditingNode(null);
      setParentForNewNode(null);
      await fetchTaxonomy();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Failed to save node:', err);
      setError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setIsSubmittingForm(false);
    }
  };

  const handleFormCancel = () => {
    setShowNodeForm(false);
    setEditingNode(null);
    setParentForNewNode(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2 dark:text-white">
            {t('settings.taxonomy.title', 'Knowledge Taxonomy')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('settings.taxonomy.loading', 'Loading taxonomy...')}
          </p>
        </div>
        <Card>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2 dark:text-white">
            {t('settings.taxonomy.title', 'Knowledge Taxonomy')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t(
              'settings.taxonomy.description',
              'Organize your content into a hierarchical knowledge structure'
            )}
          </p>
        </div>
        {nodes.length > 0 && (
          <div className="flex gap-2">
            <AITaxonomyGenerator
              onTaxonomyGenerated={fetchTaxonomy}
              disabled={!!activeJobId}
              onJobStarted={(jobId) => setActiveJobId(jobId)}
            />
            <Button onClick={handleCreateFirstNode}>
              {t('settings.taxonomy.createCategory', 'New Category')}
            </Button>
          </div>
        )}
      </div>

      {/* Active Job Progress */}
      {activeJobId && (
        <TaxonomyJobProgress
          jobId={activeJobId}
          onComplete={() => {
            setActiveJobId(null);
            fetchTaxonomy();
          }}
          onCancel={() => {
            setActiveJobId(null);
          }}
        />
      )}

      {/* Success/Error Messages */}
      {error && (
        <div className="p-3 border-2 border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200 text-sm">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="p-3 border-2 border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-200 text-sm">
          {successMessage}
        </div>
      )}

      {/* Empty State */}
      {nodes.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold mb-2 dark:text-white">
              {t('settings.taxonomy.emptyTitle', 'No taxonomy structure yet')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              {t(
                'settings.taxonomy.emptyDescription',
                'Build your knowledge taxonomy by automatically discovering content from your website or manually creating categories.'
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <AITaxonomyGenerator
                onTaxonomyGenerated={fetchTaxonomy}
                disabled={!!activeJobId}
                onJobStarted={(jobId) => setActiveJobId(jobId)}
              />
              <Button variant="secondary" onClick={handleCreateFirstNode}>
                {t('settings.taxonomy.createFirstCategory', 'Create First Category')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Tree View */}
      {nodes.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          {/* Left: Tree View */}
          <div className="xl:col-span-2">
            <Card>
              <h2 className="text-xl font-bold mb-4 dark:text-white">
                {t('settings.taxonomy.treeTitle', 'Taxonomy Structure')}
              </h2>
              <div className="max-h-[600px] overflow-y-auto pr-2">
                <TaxonomyTree
                  nodes={nodes}
                  selectedNodeId={selectedNode?.id || null}
                  onSelectNode={setSelectedNode}
                  onCreateChild={handleCreateChild}
                />
              </div>
            </Card>
          </div>

          {/* Right: Node Details */}
          <div className="xl:col-span-3">
            <Card>
              <div className="max-h-[600px] overflow-y-auto pr-2">
                <TaxonomyNodeCard
                  node={selectedNode}
                  onEdit={handleEditNode}
                  onDelete={async (node) => {
                    try {
                      await apiClient.delete(`/taxonomy/${node.id}`);
                      setSuccessMessage(`Node "${node.name}" deleted successfully`);
                      setSelectedNode(null);
                      fetchTaxonomy();
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (err: any) {
                      console.error('Failed to delete node:', err);
                      setError(err.response?.data?.message || 'Failed to delete node');
                    }
                  }}
                  onKeywordsUpdated={() => {
                    fetchTaxonomy();
                  }}
                  onContentLinked={() => {
                    fetchTaxonomy();
                  }}
                  onGenerateEmbedding={async (node) => {
                    try {
                      await apiClient.post(`/taxonomy/${node.id}/embedding`);
                      setSuccessMessage('Embedding generated successfully');
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (err: any) {
                      console.error('Failed to generate embedding:', err);
                      setError(err.response?.data?.message || 'Failed to generate embedding');
                    }
                  }}
                />
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Summary */}
      {nodes.length > 0 && (
        <Card>
          <div className="flex justify-between items-center text-sm">
            <p className="text-gray-600 dark:text-gray-400">
              {t('settings.taxonomy.summary', 'Total: {{count}} categories', { count: nodes.length })}
            </p>
          </div>
        </Card>
      )}

      {/* Node Form Modal */}
      {showNodeForm && (
        <TaxonomyNodeForm
          node={editingNode}
          parentNode={parentForNewNode}
          allNodes={nodes}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isSubmitting={isSubmittingForm}
        />
      )}
    </div>
  );
}
